/**
 * Removes the last empty parameter from an array of parameters or a string.
 *
 * @template T Type of input (string or string array)
 * @param {T} params Input parameters
 * @returns {T | string[]} Parameters with last empty element removed if applicable
 * @example
 * removeLastEmptyParam(['a', 'b', '']); // Returns ['a', 'b']
 * removeLastEmptyParam('example'); // Returns 'example'
 */
export const removeLastEmptyParam = <T extends string | string[]>(
  params: T
): T | string[] =>
  Array.isArray(params) && params?.at(-1) === '' ? params.slice(0, -1) : params

/**
 * Converts an absolute URL to a relative URL by extracting pathname, search, and hash.
 *
 * @param {string} url The absolute URL to convert
 * @returns {string} The relative URL with pathname, search, and hash
 * @throws {TypeError} If the URL is invalid
 * @throws {Error} If the URL cannot be parsed
 * @example
 * toRelativeUrl('https://example.com/path?query=value#hash');
 * // Returns '/path?query=value#hash'
 */
export const toRelativeUrl = (url: string): string => {
  const { pathname, search, hash } = new URL(url)
  return `${pathname}${search}${hash}`.replaceAll(/,/g, '')
}
